import React, { useEffect, useRef, useState } from 'react';
import { string } from 'prop-types';
import {
  DayPickerRangeController,
  isSameDay,
  isInclusivelyAfterDay,
} from 'react-dates';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { moment } from '../../util/dates';
import { compose } from 'redux';
import { connect } from 'react-redux';
import memoize from 'lodash/memoize';

import {
  addAvailabilityExceptionsRequest,
} from '../../containers/EditListingPage/EditListingPage.duck';
import { fetchTimeSlots } from '../../containers/ListingPage/ListingPage.duck';
import {
  START_DATE,
  END_DATE,
  monthIdString,
  getSlotHour,
  morningSlotValue,
  eveningSlotValue,
} from '../../util/dates';
import { IconArrowHead, IconSpinner } from '../../components';

import Day from './Day';
import css from './ManageAvailabilityCalendar.css';

// Constants

const HORIZONTAL_ORIENTATION = 'horizontal';

// Constants for calculating day width (aka table cell dimensions)
const TABLE_BORDER = 2;
const TABLE_COLUMNS = 7;
const MIN_CONTENT_WIDTH = 272;
const MIN_CELL_WIDTH = Math.floor(MIN_CONTENT_WIDTH / TABLE_COLUMNS); // 38
const MAX_CONTENT_WIDTH_DESKTOP = 480;
const MAX_CELL_WIDTH_DESKTOP = Math.floor(MAX_CONTENT_WIDTH_DESKTOP / TABLE_COLUMNS); // 108
const VIEWPORT_LARGE = 1024;

// Helper functions

// Calculate the width for a calendar day (table cell)
const dayWidth = (wrapperWidth, windowWith) => {
  if (windowWith >= VIEWPORT_LARGE) {
    // NOTE: viewportLarge has a layout with sidebar.
    // In that layout 271 px is reserved for sidebar and gutter.
    const width = windowWith * 1 - 271;
    return width > MAX_CONTENT_WIDTH_DESKTOP
      ? MAX_CELL_WIDTH_DESKTOP
      : Math.floor((width - TABLE_BORDER) / TABLE_COLUMNS);
  } else {
    return wrapperWidth > MIN_CONTENT_WIDTH
      ? Math.floor((wrapperWidth - TABLE_BORDER) / TABLE_COLUMNS)
      : MIN_CELL_WIDTH;
  }
};

// Get a function that returns the start of the previous month
const prevMonthFn = currentMoment =>
  currentMoment
    .clone()
    .subtract(1, 'months')
    .startOf('month');

// Get a function that returns the start of the next month
const nextMonthFn = currentMoment =>
  currentMoment
    .clone()
    .add(1, 'months')
    .startOf('month');

const isDateOutsideRange = date => {
  return (
    moment(date).isBefore(moment(), 'day') || isInclusivelyAfterDay(date, moment().add(180, 'd'))
  );
};
const isOutsideRange = memoize(isDateOutsideRange);

const dateModifiers = (date) => {
  const today = moment().startOf('day');
  return {
    isOutsideRange: isOutsideRange(date),
    isSameDay: isSameDay(date, today),
  };
};

const ManageAvailabilityCalendar = props => {
  const {
    className,
    rootClassName,
    addSlotsInProgress,
    addSlotsSuccess,
    addSlotsError,
    onRequestTimeslots,
    onAddAvailabilityPlanRequest,
    onAddAvailabilityExceptionsRequest,
    fetchTimeSlotsInProgress,
    fetchTimeSlotsError,
    timeSlots,
    listingId,
    onMonthChanged,
    monthFormat,
    autoFocusEndDate,
    intl,
    ...rest,
  } = props;
  const [dayPickerWrapper, setDayPickerWrapper] = useState(null);
  const [dayPicker, setDayPicker] = useState(null);
  const [focusedInput, setFocusedInput] = useState(autoFocusEndDate ? END_DATE : START_DATE);
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startHour, setStartHour] = useState(getSlotHour(morningSlotValue, true));
  const [endHour, setEndHour] = useState(getSlotHour(eveningSlotValue, false));
  const prevMonth = useRef();

  useEffect(() => {
    if (!listingId || moment(currentMonth).isBefore(moment(), 'month')) return;

    onRequestTimeslots(listingId, currentMonth);
  }, [listingId, currentMonth]);

  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    setFocusedInput(START_DATE);
  }, [addSlotsSuccess, addSlotsError]);

  const onDatesChange = ({ startDate, endDate }) => {
    if (startDate !== endDate) {
      setStartDate(startDate);
    }
    setEndDate(endDate);
  };
  const handleAvailability = (availability) => () => {
    const isAvailable = availability;
    const startDateWithHour = moment(startDate).set('hour', startHour);
    const endDateWithHour = moment(endDate).set('hour', endHour);
    const maxDateShown = moment(currentMonth).endOf('month').format();

    onAddAvailabilityExceptionsRequest(listingId, startDateWithHour.toISOString(), endDateWithHour.toISOString(), isAvailable, maxDateShown);
  };
  const onFocusChange = (focusedInput) => {
    setFocusedInput(!focusedInput ? START_DATE : focusedInput);
  };
  const onMonthClick = (monthFn) => {
    prevMonth.current = currentMonth;
    setCurrentMonth(monthFn(prevMonth.current));
    setStartDate(null);
    setEndDate(null);
    setFocusedInput(START_DATE);
    if (onMonthChanged) {
      onMonthChanged(monthIdString(currentMonth));
    }
  };

  const { clientWidth: width } = dayPickerWrapper || { clientWidth: 0 };
  const hasWindow = typeof window !== 'undefined';
  const windowWidth = hasWindow ? window.innerWidth : 0;

  const daySize = dayWidth(width, windowWidth);
  const calendarGridWidth = daySize * TABLE_COLUMNS + TABLE_BORDER;

  const renderMonthElement = ({ month }) => (
    <div className={css.monthElement}>
      <span className={css.monthString}>{month.format(monthFormat)}</span>
      {fetchTimeSlotsInProgress ? <IconSpinner rootClassName={css.monthInProgress} /> : null}
    </div>
  );
  const renderDayContents = (day) => {
    const dayIsSelected = day === startDate || day === endDate;
    const dayIsInSelection = moment(day).isBetween(startDate, endDate);
    const isEndDate = day === endDate;
    return (
      <Day
        className={css.dayContainer}
        dateModifiers={dateModifiers}
        date={day}
        isSelected={dayIsSelected}
        isInSelection={dayIsInSelection}
        isEndDate={isEndDate}
        timeSlots={timeSlots}
        setStartDate={setStartDate}
        startDate={startDate}
        endDate={endDate}
        setStartHour={setStartHour}
        setEndHour={setEndHour}
        handleAvailability={handleAvailability}
        intl={intl}
      />
    )
  };

  const startText = intl.formatMessage({ id: 'ManageAvailabilityCalendar.start' });
  const endText = intl.formatMessage({ id: 'ManageAvailabilityCalendar.end' });
  const startDateFormatted = moment(startDate).format('D MMMM');
  const endDateFormatted = moment(endDate).format('D MMMM');

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div
      className={classes}
      ref={c => {
        setDayPickerWrapper(c);
      }}
    >
      <div className={css.helperContainer} style={{ width: `${calendarGridWidth}px` }}>
        <FormattedMessage id='ManageAvailabilityCalendar.helperText' />
        <div className={css.legendContainer}>
        <div className={css.legend}>
          <div className={css.legendRow}>
            <span className={css.legendAvailableColor} />
            <span className={css.legendText}>
              <FormattedMessage id="ManageAvailabilityCalendar.availableDay" />
            </span>
          </div>
          <div className={css.legendRow}>
            <span className={css.legendBlockedColor} />
            <span className={css.legendText}>
              <FormattedMessage id="ManageAvailabilityCalendar.blockedDay" />
            </span>
          </div>
        </div>
        </div>
      </div>
      <div className={css.datesContainer}>
        <div className={css.startDateContainer}>
          <span>{startText}</span>
          <span>{startDate ? startDateFormatted : '...'}</span>
        </div>
        <div className={css.endDateContainer}>
          <span>{endText}</span>
          <span>{endDate ? endDateFormatted : '...'}</span>
        </div>
      </div>
      {width > 0 ? (
        <div style={{ width: `${calendarGridWidth}px` }}>
          <DayPickerRangeController
            {...rest}
            ref={c => {
              setDayPicker(c);
            }}
            numberOfMonths={1}
            navPrev={<IconArrowHead direction="left" />}
            navNext={<IconArrowHead direction="right" />}
            weekDayFormat="ddd"
            daySize={daySize}
            renderDayContents={renderDayContents}
            enableOutsideDays={false}
            focusedInput={focusedInput}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDatesChange}
            onFocusChange={onFocusChange}
            onPrevMonthClick={() => onMonthClick(prevMonthFn)}
            onNextMonthClick={() => onMonthClick(nextMonthFn)}
            hideKeyboardShortcutsPanel
            horizontalMonthPadding={9}
            renderMonthElement={renderMonthElement}
          />
        </div>
      ) : null}
    </div>
  );
}

ManageAvailabilityCalendar.defaultProps = {
  className: null,
  rootClassName: null,
  addSlotsInProgress: false,
  addSlotsSuccess: false,
  addSlotsError: false,
  timeSlots: {},

  autoFocusEndDate: false,

  // day presentation and interaction related props
  renderCalendarDay: undefined,
  renderDayContents: null,
  minimumNights: 0,
  isDayBlocked: () => false,
  isOutsideRange,
  isDayHighlighted: () => false,
  enableOutsideDays: true,

  // calendar presentation and interaction related props
  orientation: HORIZONTAL_ORIENTATION,
  withPortal: false,
  initialVisibleMonth: null,
  numberOfMonths: 2,
  onOutsideClick() {},
  keepOpenOnDateSelect: false,
  renderCalendarInfo: null,
  isRTL: false,

  // navigation related props
  navPrev: null,
  navNext: null,
  onPrevMonthClick() {},
  onNextMonthClick() {},

  // internationalization
  monthFormat: 'MMMM YYYY',
  onMonthChanged: null,
};

ManageAvailabilityCalendar.propTypes = {
  className: string,
  rootClassName: string,
};

const mapStateToProps = state => {
  const { addSlotsInProgress, addSlotsSuccess, addSlotsError } = state.EditListingPage;
  const { timeSlots, fetchTimeSlotsInProgress, fetchTimeSlotsError } = state.ListingPage;
  return {
    timeSlots,
    fetchTimeSlotsInProgress,
    fetchTimeSlotsError,
    addSlotsInProgress,
    addSlotsSuccess,
    addSlotsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onRequestTimeslots: (listingId, monthsToFetchIndex) =>
    dispatch(fetchTimeSlots(listingId, monthsToFetchIndex)),
  onAddAvailabilityExceptionsRequest: (listingId, startDate, endDate, isAvailable, maxDateShown) =>
    dispatch(addAvailabilityExceptionsRequest(listingId, startDate, endDate, isAvailable, maxDateShown)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), injectIntl)(ManageAvailabilityCalendar);



// WEBPACK FOOTER //
// ./src/components/ManageAvailabilityCalendar/ManageAvailabilityCalendar.js